/* Haavi product page — <ho-product-main>.
   Owns the product form: variant/size selection (client-side from a pre-formatted variants JSON),
   quantity stepper, gallery thumbnail + variant-image sync, and AJAX add-to-cart that reuses
   Horizon's cart system (dispatches the standard CartLinesUpdateEvent, posts to /cart/add.js with
   the cart-drawer section, then opens the drawer). The Buy-It-Now payment button submits natively.
   Size buttons are an ARIA radiogroup (roving tabindex + arrow keys). */
if (!customElements.get('ho-product-main')) {
  customElements.define('ho-product-main', class extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.form = this.querySelector('form.ho-pp__form');
      this.idInput = this.querySelector('[data-variant-id]');
      this.addBtn = this.querySelector('[data-add-btn]');
      this.addLabel = this.querySelector('[data-add-label]');
      this.addPrice = this.querySelector('[data-add-price]');
      this.priceNow = this.querySelector('[data-price-now]');
      this.priceWas = this.querySelector('[data-price-was]');
      this.priceOff = this.querySelector('[data-price-off]');
      this.saveTag = this.querySelector('[data-save-tag]');
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.mainImg = this.querySelector('[data-gallery-img]');
      this.sticky = this.querySelector('[data-sticky]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.stickySize = this.querySelector('[data-sticky-size]');
      this.stickyAddBtn = this.querySelector('[data-sticky-add]');

      this.addLabelText = this.dataset.addLabel || (this.addLabel ? this.addLabel.textContent.trim() : 'Add to Cart');
      this.soldoutText = this.dataset.soldoutLabel || 'Sold Out';
      this.cur = this.dataset.cur || '';
      this.savePrefix = this.dataset.savePrefix || 'Save';

      try { this.variants = JSON.parse(this.querySelector('script[data-variants]').textContent); }
      catch (e) { this.variants = []; }

      // Option (size) radiogroups
      this.selected = [];
      this.querySelectorAll('.ho-pp__opt').forEach((opt) => {
        const idx = parseInt(opt.dataset.optionIndex, 10);
        const active = opt.querySelector('.ho-pp__size.is-active') || opt.querySelector('.ho-pp__size');
        if (active) this.selected[idx] = active.dataset.value;
        const btns = [...opt.querySelectorAll('.ho-pp__size')];
        btns.forEach((btn) => {
          btn.addEventListener('click', () => this._pick(idx, btn));
          btn.addEventListener('keydown', (e) => this._radioKey(e, idx, btns, btn));
        });
      });

      // Thumbnails
      this.querySelectorAll('[data-thumbs] button').forEach((b) => {
        b.addEventListener('click', () => this._setImage(b.dataset.full, b.dataset.mediaId, b.dataset.alt));
      });

      // Quantity
      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.addEventListener('click', () => this._qty(-1));
      if (plus) plus.addEventListener('click', () => this._qty(1));

      // Add to cart (form submit or sticky button)
      if (this.form) this.form.addEventListener('submit', (e) => { e.preventDefault(); this._add(); });
      if (this.stickyAddBtn) this.stickyAddBtn.addEventListener('click', () => this._add());
      this._observeSticky();

      // Zoom lightbox
      if (this.dataset.zoom === 'true') {
        const main = this.querySelector('[data-zoomable]');
        if (main) {
          main.addEventListener('click', () => this._openZoom());
          main.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._openZoom(); } });
        }
      }
    }

    disconnectedCallback() {
      if (this._io) { this._io.disconnect(); this._io = null; }
    }

    _openZoom() {
      if (!this.mainImg) return;
      const opener = document.activeElement;
      const pageMain = document.querySelector('main, #MainContent');
      const box = document.createElement('div');
      box.className = 'ho-pp__lightbox';
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', this.mainImg.alt || 'Product image');
      const img = document.createElement('img');
      img.src = this.mainImg.src; img.alt = this.mainImg.alt || '';
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'ho-pp__lightbox-close';
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.innerHTML = '&times;';
      box.appendChild(img);
      box.appendChild(closeBtn);
      const close = () => {
        box.remove();
        if (pageMain) { pageMain.removeAttribute('inert'); pageMain.removeAttribute('aria-hidden'); }
        document.removeEventListener('keydown', onKey);
        if (opener && opener.focus) opener.focus();
      };
      const onKey = (e) => {
        if (e.key === 'Escape') close();
        else if (e.key === 'Tab') { e.preventDefault(); closeBtn.focus(); }
      };
      box.addEventListener('click', (e) => { if (e.target === box || e.target === closeBtn) close(); });
      document.addEventListener('keydown', onKey);
      document.body.appendChild(box);
      if (pageMain) { pageMain.setAttribute('inert', ''); pageMain.setAttribute('aria-hidden', 'true'); }
      closeBtn.focus();
    }

    _pick(idx, btn) {
      if (btn.hasAttribute('data-soldout')) return;
      const opt = btn.closest('.ho-pp__opt');
      opt.querySelectorAll('.ho-pp__size').forEach((b) => {
        b.classList.remove('is-active'); b.setAttribute('aria-checked', 'false'); b.tabIndex = -1;
      });
      btn.classList.add('is-active'); btn.setAttribute('aria-checked', 'true'); btn.tabIndex = 0;
      this.selected[idx] = btn.dataset.value;
      const sel = opt.querySelector('[data-opt-selected]');
      if (sel) {
        const u = btn.querySelector('.s-unit');
        const ut = u ? u.textContent.trim() : '';
        sel.textContent = btn.dataset.value + (ut ? ' · ' + ut : '');
      }
      this._resolve();
    }

    _radioKey(e, idx, btns, btn) {
      const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
      if (!(e.key in keys)) return;
      e.preventDefault();
      const selectable = btns.filter((b) => !b.hasAttribute('data-soldout'));
      if (!selectable.length) return;
      let i = selectable.indexOf(btn);
      if (i === -1) i = 0;
      const next = selectable[(i + keys[e.key] + selectable.length) % selectable.length];
      next.focus();
      this._pick(idx, next);
    }

    _resolve() {
      const key = this.selected.join('~~');
      const v = this.variants.find((x) => x.options.join('~~') === key);
      if (v) this._apply(v);
      else this._unavailable();
    }

    _apply(v) {
      if (this.idInput) { this.idInput.value = v.id; this.idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      const saveTxt = this.savePrefix + ' ' + v.save + '%';
      if (this.priceNow) {
        this.priceNow.textContent = '';
        const curSpan = document.createElement('span');
        curSpan.className = 'ho-pp__cur';
        curSpan.textContent = this.cur;
        this.priceNow.appendChild(curSpan);
        this.priceNow.appendChild(document.createTextNode(v.price));
      }
      if (this.priceWas) { if (v.has_compare) { this.priceWas.textContent = this.cur + v.compare; this.priceWas.hidden = false; } else { this.priceWas.hidden = true; } }
      if (this.priceOff) { if (v.has_compare && v.save) { this.priceOff.textContent = saveTxt; this.priceOff.hidden = false; } else { this.priceOff.hidden = true; } }
      if (this.saveTag) { if (v.has_compare && v.save) { this.saveTag.textContent = saveTxt; this.saveTag.hidden = false; } else { this.saveTag.hidden = true; } }
      if (this.addPrice) this.addPrice.textContent = this.cur + v.price;
      if (this.stickyPrice) this.stickyPrice.textContent = this.cur + v.price;
      if (this.stickySize && v.title) this.stickySize.textContent = v.title;
      this._setAvailable(v.available);
      if (v.media_src && this.mainImg) this._setImage(v.media_src, v.media_id, v.media_alt);
      try { const u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u); } catch (e) {}
    }

    _unavailable() {
      if (this.idInput) { this.idInput.value = ''; this.idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (this.addPrice) this.addPrice.textContent = '';
      if (this.stickyPrice) this.stickyPrice.textContent = '';
      if (this.priceWas) this.priceWas.hidden = true;
      if (this.priceOff) this.priceOff.hidden = true;
      this._setAvailable(false, this.dataset.unavailableLabel || this.soldoutText);
    }

    _setAvailable(available, soldLabel) {
      const label = available ? this.addLabelText : (soldLabel || this.soldoutText);
      if (this.addBtn) { this.addBtn.disabled = !available; }
      if (this.addLabel) this.addLabel.textContent = label;
      if (this.stickyAddBtn) {
        this.stickyAddBtn.disabled = !available;
        this.stickyAddBtn.setAttribute('aria-disabled', String(!available));
        this.stickyAddBtn.textContent = label;
      }
      this.querySelectorAll('.ho-pp__buynow-wrap button, .ho-pp__buynow-wrap .shopify-payment-button__button').forEach((b) => {
        b.disabled = !available;
        b.setAttribute('aria-disabled', String(!available));
      });
    }

    _setImage(src, mediaId, alt) {
      if (this.mainImg && src) this.mainImg.src = src;
      if (this.mainImg && alt != null) this.mainImg.alt = alt;
      this.querySelectorAll('[data-thumbs] button').forEach((b) => b.classList.toggle('is-active', String(b.dataset.mediaId) === String(mediaId)));
    }

    _qty(d) {
      if (!this.qtyInput) return;
      this.qtyInput.value = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + d);
    }

    _observeSticky() {
      if (!this.sticky || !this.addBtn || !('IntersectionObserver' in window)) return;
      this._io = new IntersectionObserver((entries) => {
        entries.forEach((en) => { this.sticky.classList.toggle('is-visible', !en.isIntersecting); });
      }, { rootMargin: '0px 0px -40px 0px' });
      this._io.observe(this.addBtn);
    }

    async _add() {
      const id = this.idInput ? this.idInput.value : null;
      const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
      if (!id || (this.addBtn && this.addBtn.disabled)) return;
      const prev = this.addLabel ? this.addLabel.textContent : '';
      if (this.addBtn) { this.addBtn.setAttribute('aria-busy', 'true'); this.addBtn.disabled = true; }
      if (this.addLabel) this.addLabel.textContent = 'Adding';

      const addUrl = this.dataset.addUrl || '/cart/add';
      const jsUrl = addUrl.endsWith('.js') ? addUrl : addUrl + '.js';
      const cartUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart';
      const drawerSection =
        (document.querySelector('cart-items-component[data-section-id]') || {}).dataset?.sectionId || 'cart-drawer-section';

      let Events = null;
      try { Events = await import('@shopify/events'); } catch (e) { Events = null; }

      let deferred = null;
      try {
        if (Events && Events.CartLinesUpdateEvent) {
          deferred = Events.CartLinesUpdateEvent.createPromise();
          this.dispatchEvent(new Events.CartLinesUpdateEvent({
            action: 'add', context: 'product',
            lines: [{ merchandiseId: String(id), quantity: qty }],
            promise: deferred.promise,
          }));
        }

        const body = new FormData();
        body.set('id', String(id));
        body.set('quantity', String(qty));
        body.set('sections', drawerSection);

        const res = await fetch(jsUrl, { method: 'POST', headers: { Accept: 'text/html' }, body, credentials: 'same-origin' });
        const added = await res.json();
        if (added.status) throw new Error(added.message || 'Add failed');

        if (deferred) {
          const cartJson = await fetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((r) => r.json());
          deferred.resolve({
            cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cartJson),
            detail: { items: cartJson.items, sections: added.sections, itemCount: qty, source: 'ho-product-main', didError: false },
          });
        }

        // Open Haavi's custom cart drawer (assets/ho-cart-drawer.js listens for 'cart:added',
        // then refreshes its contents from the Section Rendering API and slides open).
        if (document.querySelector('[data-cart-drawer]')) {
          document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: { source: 'ho-product' } }));
        } else {
          window.location = cartUrl;
        }

        if (this.addLabel) this.addLabel.textContent = 'Added';
        setTimeout(() => { if (this.addLabel) this.addLabel.textContent = this.addLabelText; }, 1600);
      } catch (err) {
        if (deferred) { try { deferred.resolve({ cart: null, detail: { source: 'ho-product-main', didError: true } }); } catch (e) {} }
        console.warn('[ho-product-main] add to cart failed', err);
        if (this.addLabel) this.addLabel.textContent = 'Try again';
        setTimeout(() => { if (this.addLabel) this.addLabel.textContent = this.addLabelText; }, 1800);
      } finally {
        if (this.addBtn) { this.addBtn.removeAttribute('aria-busy'); this.addBtn.disabled = false; }
      }
    }
  });
}
